import React from 'react';
import { Clock } from 'lucide-react';

/**
 * Cadrul pasului 3 din inscriere — tot ce e IDENTIC in cele trei stari ale
 * ecranului de video (`se-incarca`, `gata`, `indisponibil`).
 *
 * 🔴 De ce exista, si de ce nu sunt trei copii: S1 a ridicat „ecranul nu tresare
 * cand soseste raspunsul" de la argument la CERINTA MASURABILA — intre
 * `se-incarca` si `gata`, `top`-ul titlului si al casetei de player nu are voie
 * sa se schimbe cu niciun pixel, masurat la 360 si la 1280. Cu doua copii ale
 * antetului, cerinta ar fi indeplinita in ziua livrarii si stricata tacut la
 * prima clasa schimbata intr-una din ele. Aici e indeplinita **prin
 * constructie**: e acelasi DOM, nu doua DOM-uri care se nimeresc egale.
 *
 * Acelasi motiv pentru care `CardProtocol` e o componenta cu `variant`, nu doua
 * cartonase la fel.
 *
 * ⚠️ Bara de pasi ramane in TOATE starile, inclusiv `indisponibil`: pozitia in
 * flux e adevarata si atunci. Ce nu ramane e pastila — vezi mai jos.
 */

interface Props {
    /**
     * Textul pastilei de durata, sau `null` ca sa NU se randeze deloc.
     *
     * 🔴 `null` nu e o comoditate, e regula lui S1 pentru starea `indisponibil`:
     * pe un ecran care spune „filmuletul nu e disponibil", nimic nu are voie sa
     * ramana promitand ceva ce nu mai vine. Pastila promite o durata ⇒ iese.
     * Titlul si paragraful descriptiv DESCRIU, nu promit ⇒ raman; fara ele
     * caseta de eroare ar pluti fara subiect.
     */
    pastila: string | null;
    /** Zona de sub paragraf: playerul, dreptunghiul gol, sau caseta de eroare. */
    children: React.ReactNode;
    /**
     * Ce sta in AFARA coloanei cu padding — azi doar bara ancorata jos, cu
     * «Continuă». E o fanta separata, nu `children`, pentru ca elementul e
     * `position: fixed`: intr-o zi in care un stramos capata `transform`, un
     * `fixed` mutat inauntru s-ar ancora de el in loc de fereastra. Structura
     * ramane cea de azi, nu una care se nimereste sa arate la fel.
     */
    subsol?: React.ReactNode;
}

export default function CadruPasVideo({ pastila, children, subsol }: Props) {
    return (
        <div className="mx-auto flex w-full max-w-2xl flex-col pb-32">
            {/* Pasii: unde suntem in inscriere. Cifra e din machetă; devine dinamica
                atunci cand pasii inscrierii vor fi cunoscuti in cod.
                Sub `md`, `pt-20` rezerva locul hamburgerului flotant din `AuthenticatedLayout`
                (masurat la 360: 52x52 la 16,16 → jos la 68 px; 80 = 68 + aceeasi distanta de
                12 px ca `pt-3`). NU se ascunde hamburgerul: e singura cale spre logout pe mobil. */}
            <div className="flex gap-1.5 px-4 pt-20 md:pt-3">
                <i className="h-1 flex-1 rounded-sm bg-magenta" />
                <i className="h-1 flex-1 rounded-sm bg-magenta" />
                <i className="h-1 flex-1 rounded-sm bg-lime-accent" />
                <i className="h-1 flex-1 rounded-sm bg-muted" />
            </div>
            <p className="px-4 pt-2 text-smaller uppercase tracking-wider text-gray-600 m-0">
                Pasul 3 din 4
            </p>

            <div className="px-4 pt-3">
                <h1 className="font-heading text-fluid-2xl font-semibold text-dark m-0">
                    Un scurt filmuleț, înainte să mergem mai departe
                </h1>

                {/* Durata se declara INAINTE de start, si nu se inventeaza niciodata
                    un numar. Textul cat timp nu se stie e «se încarcă…», UNUL SINGUR
                    pentru ambele faze in care nu se stie (adresa nu a sosit inca /
                    playerul n-a raportat durata): era «se încarcă durata…», adica o
                    promisiune de durata facuta inainte sa existe macar un film. */}
                {pastila !== null && (
                    <p className="mt-2.5 inline-flex items-center gap-1.5 rounded-full border border-hairline bg-white px-3 py-1 text-small text-dark m-0">
                        <Clock className="size-3.5" aria-hidden />
                        {pastila}
                    </p>
                )}

                <p className="mb-3.5 mt-3 text-fluid-base text-dark m-0">
                    Explică pe scurt cum se colectează în siguranță bateriile cu litiu. Îl vezi o
                    singură dată, la înscriere.
                </p>

                {children}
            </div>

            {subsol}
        </div>
    );
}
